import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from '../../services/axiosInstance';
import maCateImg from "../../assets/img/m-a-cate.png";
import TopBtn from "../../components/web/layoutsadmin/TopBtn";
import MsgSelectModal from "../../components/web/modal/MsgSelectModal";
import {AiOutlineUser, AiOutlineUserAdd, AiOutlineUserDelete, AiOutlineUsergroupAdd} from "react-icons/ai";
import {FiSearch} from "react-icons/fi";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  관리자 [상점관리 > 상점정보]
 * ════════════════════════════════════════════════════════════════════════════════
 *  - 가입한 상점사(쇼핑몰) 전체목록 (page 셀렉트박스 없이 항상 전체조회)
 *  - 화면에 출력된 컬럼값으로 필터되는 서치인풋
 *  - 상단에 상점사 전용 total / follow / loss / today 카운트
 *    (비토리지 고객 카운트와 완전히 분리된 집계다)
 *  - activate_fl on/off 핸들링
 *  - 행 클릭 시 해당 상점의 날짜별 총매출/총주문/총유입고객/오늘유입고객 상세 드랍다운
 *    (기본 조회기간 : 한국시간 기준 1년 전 ~ 오늘)
 * ════════════════════════════════════════════════════════════════════════════════
 */
function AdminStoreInfoPg() {

    const LCAT = localStorage.getItem('adminToken');
    const authHeader = {headers: {Authorization: LCAT}};

    const [storeList, setStoreList] = useState([]);
    const [countData, setCountData] = useState({total: 0, follow: 0, loss: 0, today: 0});
    const [searchInput, setSearchInput] = useState('');
    const [expandedSeq, setExpandedSeq] = useState(null);
    const [detailMap, setDetailMap] = useState({});     // seqNum -> StoreDashboardDTO
    const [dateMap, setDateMap] = useState({});         // seqNum -> {startDate, endDate}
    const [isLoading, setIsLoading] = useState(false);

    /* ── 활성/비활성 변경 확인 알럿 ───────────────────────────── */
    const [alertShow, setAlertShow] = useState(false);
    const [pendingActivate, setPendingActivate] = useState(null); // {seqNum, next, companyName}
    const modalCancel = useCallback(() => setAlertShow(false), []);

    // ────────────────────────────────────────────────────────────────────────
    //  조회
    // ────────────────────────────────────────────────────────────────────────

    const selectStoreList = () => {
        setIsLoading(true);
        axios.get('/store/admin/list', authHeader)
            .then((res) => setStoreList(res.data || []))
            .catch(() => setStoreList([]))
            .finally(() => setIsLoading(false));
    };

    const selectStoreCount = () => {
        axios.get('/store/admin/count', authHeader)
            .then((res) => setCountData(res.data || {total: 0, follow: 0, loss: 0, today: 0}))
            .catch(() => {});
    };

    useEffect(() => {
        selectStoreList();
        selectStoreCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** 기본 조회기간 : 한국시간 기준 1년 전 ~ 오늘 (yyyymmdd) */
    const kstYmd = (offsetYear) => {
        const now = new Date(new Date().toLocaleString('en-US', {timeZone: 'Asia/Seoul'}));
        if (offsetYear) now.setFullYear(now.getFullYear() + offsetYear);
        const m = `${now.getMonth() + 1}`.padStart(2, '0');
        const d = `${now.getDate()}`.padStart(2, '0');
        return `${now.getFullYear()}${m}${d}`;
    };

    const defaultRange = () => ({startDate: kstYmd(-1), endDate: kstYmd(0)});

    const selectDetail = (seqNum, range) => {
        const r = range || dateMap[seqNum] || defaultRange();
        axios.get('/store/admin/dashboard', {
            ...authHeader,
            params: {seqNum, startDate: r.startDate, endDate: r.endDate},
        })
            .then((res) => setDetailMap((prev) => ({...prev, [seqNum]: res.data})))
            .catch(() => setDetailMap((prev) => ({...prev, [seqNum]: null})));
    };

    const rowClick = (seqNum) => {
        if (expandedSeq === seqNum) {
            setExpandedSeq(null);
            return;
        }
        setExpandedSeq(seqNum);
        if (!dateMap[seqNum]) setDateMap((prev) => ({...prev, [seqNum]: defaultRange()}));
        selectDetail(seqNum, dateMap[seqNum] || defaultRange());
    };

    const dateChange = (seqNum, key, value) => {
        const next = {...(dateMap[seqNum] || defaultRange()), [key]: value.replace(/-/g, '')};
        setDateMap((prev) => ({...prev, [seqNum]: next}));
        selectDetail(seqNum, next);
    };

    // ────────────────────────────────────────────────────────────────────────
    //  활성/비활성
    // ────────────────────────────────────────────────────────────────────────

    const activateAlert = (store, next) => {
        setPendingActivate({seqNum: store.seqNum, next, companyName: store.companyName});
        setAlertShow(true);
    };

    const activateUpdate = () => {
        if (!pendingActivate) return;
        const {seqNum, next} = pendingActivate;
        axios.post('/store/admin/activate', null, {...authHeader, params: {seqNum, activateFl: next}})
            .then(() => {
                setStoreList((prev) => prev.map((s) => s.seqNum === seqNum ? {...s, activateFl: next} : s));
            })
            .catch(() => {})
            .finally(() => {
                setAlertShow(false);
                setPendingActivate(null);
            });
    };

    // ────────────────────────────────────────────────────────────────────────
    //  검색 (화면에 출력된 컬럼값 기준)
    // ────────────────────────────────────────────────────────────────────────

    const searchData = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        if (!q) return storeList;
        return storeList.filter((s) => {
            const secession = s.secessionFl ? '탈퇴' : '정상';
            const activate = s.activateFl ? '활성' : '비활성';
            return [s.email, s.companyName, s.storeUrl, s.name, s.phone, secession, activate]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q));
        });
    }, [storeList, searchInput]);

    /**
     * 자사 URL 이 실제 주소형태인지 판별
     * - 앱 서비스인 상점사는 URL 대신 '앱 이름'을 입력하므로 링크로 열지 않는다.
     */
    const isUrl = (v) => {
        if (!v) return false;
        const t = String(v).trim();
        return /^https?:\/\//i.test(t) || /^www\./i.test(t) || /^[^\s/]+\.[a-z]{2,}(\/|$)/i.test(t);
    };

    const openStoreUrl = (v) => {
        if (!isUrl(v)) return;
        const t = String(v).trim();
        const href = /^https?:\/\//i.test(t) ? t : `https://${t}`;
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    const ymdDash = (v) => (v && v.length === 8) ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}` : '';
    const comma = (v) => Number(v || 0).toLocaleString();

    return (
        <div className="AdminStoreInfoPg">
            <div className="admin-work-title">
                <img className="wd-35 ht-35" alt="" src={maCateImg}/>
                <p className="p-t-6">상점정보</p>
            </div>

            <div className="admin-wrap font-t-cont">
                <div className="admin-wrap-card">

                    {/* ══ 상점사 전용 카운트 (비토리지 고객 카운트와 분리) ══ */}
                    <div style={{display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "18px"}}>
                        <CountCard icon={<AiOutlineUser/>} label="Total" value={comma(countData.total)} tone="#7C9CF5"/>
                        <CountCard icon={<AiOutlineUserAdd/>} label="Follow" value={comma(countData.follow)} tone="#7FCBA4"/>
                        <CountCard icon={<AiOutlineUserDelete/>} label="Loss" value={comma(countData.loss)} tone="#F09C9C"/>
                        <CountCard icon={<AiOutlineUsergroupAdd/>} label="Today" value={comma(countData.today)} tone="#FFC978"/>
                    </div>

                    {/* ══ 검색 ══ */}
                    <div className="admin-event-wrap" style={{marginBottom: "10px"}}>
                        <div className="admin-event-wrap-btn"/>
                        <div className="admin-event-wrap-btn gap10">
                            <div style={{
                                display: "flex", alignItems: "center", gap: "7px",
                                border: "1px solid #DFDFDF", borderRadius: "8px", padding: "0 12px", height: "34px",
                            }}>
                                <FiSearch style={{opacity: 0.5, flex: "0 0 auto"}}/>
                                <input className="font-s-13" placeholder="상호명 / 이메일 / 담당자 / 연락처 / 상태"
                                       value={searchInput} maxLength={40}
                                       onChange={(e) => setSearchInput(e.target.value)}
                                       style={{border: "none", outline: "none", background: "transparent", width: "260px"}}/>
                            </div>
                        </div>
                    </div>

                    {/* ══ 목록 ══ */}
                    <div className="div-tb">
                        <div className="div-tb-head ht-60">
                            <div className="wd-60"><p>번호</p></div>
                            <div className="wd-220"><p>상호명</p></div>
                            <div className="wd-260"><p>이메일</p></div>
                            <div className="wd-220"><p>자사 URL</p></div>
                            <div className="wd-140"><p>담당자성함</p></div>
                            <div className="wd-160"><p>담당자연락처</p></div>
                            <div className="wd-100"><p>탈퇴여부</p></div>
                            <div className="wd-140"><p>활성화여부</p></div>
                        </div>

                        {searchData.length === 0 ?
                            <div className="div-tb-body ht-70">
                                <div className="wd-100pc text-center">
                                    <p>{isLoading ? '조회중입니다' : '조회된 상점사가 없습니다'}</p>
                                </div>
                            </div>
                            :
                            searchData.map((s, i) => (
                                <div key={s.seqNum}>
                                    <div className="div-tb-body ht-70 c-point" onClick={() => rowClick(s.seqNum)}>
                                        <div className="wd-60"><p>{i + 1}</p></div>
                                        <div className="wd-220 admin-tb-text-cut"><p className="font-b">{s.companyName}</p></div>
                                        <div className="wd-260 admin-tb-text-cut"><p>{s.email}</p></div>
                                        <div className="wd-220 admin-tb-text-cut" onClick={(ev) => ev.stopPropagation()}>
                                            {s.storeUrl ?
                                                <p onClick={() => openStoreUrl(s.storeUrl)}
                                                   title={s.storeUrl}
                                                   style={{
                                                       color: isUrl(s.storeUrl) ? "#E0932B" : undefined,
                                                       textDecoration: isUrl(s.storeUrl) ? "underline" : "none",
                                                       cursor: isUrl(s.storeUrl) ? "pointer" : "default",
                                                   }}>{s.storeUrl}</p>
                                                : <p>-</p>}
                                        </div>
                                        <div className="wd-140"><p>{s.name}</p></div>
                                        <div className="wd-160"><p>{s.phone}</p></div>
                                        <div className="wd-100"><p>{s.secessionFl ? 'Y:탈퇴' : 'N:정상'}</p></div>
                                        <div className="wd-140" onClick={(e) => e.stopPropagation()}>
                                            <StatePill on={!!s.activateFl}
                                                       onLabel="활성" offLabel="비활성"
                                                       onClick={() => activateAlert(s, !s.activateFl)}/>
                                        </div>
                                    </div>

                                    {/* ══ 상세 드랍다운 ══ */}
                                    {expandedSeq === s.seqNum &&
                                        <StoreDetailPanel
                                            store={s}
                                            detail={detailMap[s.seqNum]}
                                            range={dateMap[s.seqNum] || defaultRange()}
                                            onDateChange={(key, value) => dateChange(s.seqNum, key, value)}
                                            ymdDash={ymdDash}
                                            comma={comma}/>
                                    }
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>

            {alertShow &&
                <MsgSelectModal alertShow={alertShow}
                                eventHandle1={modalCancel}
                                eventHandle2={activateUpdate}
                                msgData1={`[${pendingActivate?.companyName}] 상점사의 API 사용상태가 변경됩니다.`}
                                msgData2={pendingActivate?.next ? '활성화 하시겠습니까?' : '비활성화 하시겠습니까?'}/>
            }
            <TopBtn/>
        </div>
    );
}

/** 상단 카운트 카드 */
function CountCard({icon, label, value, tone}) {
    return (
        <div style={{
            flex: "1 1 160px", minWidth: "160px", borderRadius: "14px", padding: "16px 18px",
            border: "1px solid #EDEDED", background: "#FFFFFF",
            boxShadow: "0 6px 18px -14px rgba(0,0,0,0.35)",
            display: "flex", alignItems: "center", gap: "12px",
        }}>
            <span style={{
                width: "38px", height: "38px", borderRadius: "12px", flex: "0 0 auto",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: `${tone}22`, color: tone, fontSize: "19px",
            }}>{icon}</span>
            <div style={{minWidth: 0}}>
                <p style={{margin: "0 0 3px", fontSize: "12px", color: "#9A9A9A", letterSpacing: "0.4px"}}>{label}</p>
                <p style={{margin: 0, fontSize: "19px", fontWeight: "bold", color: "#3A3A3A"}}>{value}</p>
            </div>
        </div>
    );
}

/**
 * 활성/비활성 상태 Pill
 * - API 키/값 영역, 영업현황의 on/off 와 일관된 파스텔 연두색/빨간색 카드형 UI
 */
function StatePill({on, onLabel, offLabel, onClick}) {
    /*
     * [주의] Common.css 의 `.div-tb-body div { display:flex; flex-direction:column; height:100% }`
     *  가 '자손 선택자' 라서 이 pill 에도 그대로 걸린다.
     *  그대로 두면 세로 70px(행 높이) / 가로 140px(칸 너비) 를 꽉 채우고
     *  점과 글자가 위아래로 쪼개진다. 아래 네 줄이 그것을 되돌린다.
     *    flexDirection : 점과 글자를 다시 가로로
     *    height/width  : 내용 크기만큼만
     *    alignSelf     : 칸 안에서 늘어나지 않고 가운데 정렬
     */
    return (
        <div onClick={onClick}
             style={{
                 cursor: "pointer", userSelect: "none",
                 display: "inline-flex", flexDirection: "row",
                 alignItems: "center", justifyContent: "center", gap: "6px",
                 height: "auto", width: "fit-content", alignSelf: "center", flex: "0 0 auto",
                 padding: "6px 13px", borderRadius: "999px",
                 background: on ? "#EAF7EC" : "#FDECEC",
                 border: `1px solid ${on ? "#BEE5C6" : "#F6C9C9"}`,
                 color: on ? "#4E9A63" : "#D06B6B",
                 fontSize: "12.5px", fontWeight: "bold",
                 transition: "background .18s, border-color .18s",
             }}>
            <span style={{
                width: "7px", height: "7px", borderRadius: "50%", flex: "0 0 auto",
                background: on ? "#7FCBA4" : "#F09C9C",
            }}/>
            {on ? onLabel : offLabel}
        </div>
    );
}

/** 상점 상세 (날짜별 총매출/총주문/총유입고객/오늘유입고객 + 인기 판매상품) */
function StoreDetailPanel({store, detail, range, onDateChange, ymdDash, comma}) {
    return (
        <div style={{
            padding: "18px 22px", background: "#FAFAFA",
            borderBottom: "1px solid #EDEDED", boxSizing: "border-box",
        }}>
            {/* 조회기간 */}
            <div style={{display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "14px"}}>
                <span className="font-b font-s-13">조회기간</span>
                <input type="date" className="admin-input-line ht-32 font-s-13" style={{padding: "0 8px"}}
                       value={ymdDash(range.startDate)}
                       onChange={(e) => onDateChange('startDate', e.target.value)}/>
                <span className="font-s-13">~</span>
                <input type="date" className="admin-input-line ht-32 font-s-13" style={{padding: "0 8px"}}
                       value={ymdDash(range.endDate)}
                       onChange={(e) => onDateChange('endDate', e.target.value)}/>
                <span className="font-s-12" style={{color: "#9A9A9A"}}>기본값 : 1년 전 ~ 오늘 (한국시간)</span>
            </div>

            {/* 집계 */}
            <div style={{display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "16px"}}>
                <MiniStat label="총매출" value={`${comma(detail?.totalSales)} KRW`}/>
                <MiniStat label="총주문 수" value={comma(detail?.totalOrderCount)}/>
                <MiniStat label="총유입고객" value={comma(detail?.totalInflowCount)}/>
                <MiniStat label="오늘유입고객" value={comma(detail?.todayInflowCount)}/>
                <MiniStat label="누적유입수" value={comma(store.inflow)}/>
            </div>

            {/* 판매상품목록 (인기상품순) */}
            <p className="font-b font-s-13" style={{margin: "0 0 8px"}}>판매상품목록 (인기상품순)</p>
            <div style={{border: "1px solid #EDEDED", borderRadius: "10px", overflow: "hidden", background: "#FFFFFF"}}>
                <div style={{
                    display: "flex", alignItems: "center", height: "40px",
                    background: "#F5F5F5", fontSize: "12.5px", fontWeight: "bold", color: "#777",
                }}>
                    <div style={{flex: "2 1 0", padding: "0 12px", minWidth: 0}}>상품명</div>
                    <div style={{flex: "1 1 0", padding: "0 12px", minWidth: 0}}>옵션</div>
                    <div style={{width: "90px", padding: "0 12px", textAlign: "right"}}>수량</div>
                    <div style={{width: "130px", padding: "0 12px", textAlign: "right"}}>금액</div>
                    <div style={{width: "70px", padding: "0 12px", textAlign: "center"}}>URL</div>
                </div>
                {(!detail || !detail.productList || detail.productList.length === 0) ?
                    <div style={{padding: "26px", textAlign: "center", color: "#AAA", fontSize: "13px"}}>
                        해당 기간에 판매된 연동상품이 없습니다
                    </div>
                    :
                    detail.productList.map((p, idx) => (
                        <div key={idx} style={{
                            display: "flex", alignItems: "center", minHeight: "42px",
                            borderTop: "1px solid #F1F1F1", fontSize: "13px",
                        }}>
                            <div style={{flex: "2 1 0", padding: "8px 12px", minWidth: 0, wordBreak: "break-all"}}>{p.name}</div>
                            <div style={{flex: "1 1 0", padding: "8px 12px", minWidth: 0, wordBreak: "break-all", color: "#888"}}>{p.option || '-'}</div>
                            <div style={{width: "90px", padding: "8px 12px", textAlign: "right"}}>{comma(p.totalQuantity)}</div>
                            <div style={{width: "130px", padding: "8px 12px", textAlign: "right"}}>{comma(p.totalAmount)}</div>
                            <div style={{width: "70px", padding: "8px 12px", textAlign: "center"}}>
                                {p.url ?
                                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                                       style={{color: "#E0932B", textDecoration: "underline"}}>이동</a>
                                    : '-'}
                            </div>
                        </div>
                    ))
                }
            </div>
        </div>
    );
}

function MiniStat({label, value}) {
    return (
        <div style={{
            flex: "1 1 150px", minWidth: "150px", background: "#FFFFFF",
            border: "1px solid #EDEDED", borderRadius: "12px", padding: "12px 14px",
        }}>
            <p style={{margin: "0 0 4px", fontSize: "11.5px", color: "#9A9A9A"}}>{label}</p>
            <p style={{margin: 0, fontSize: "15.5px", fontWeight: "bold", color: "#3A3A3A", wordBreak: "break-all"}}>{value}</p>
        </div>
    );
}

export default AdminStoreInfoPg;
